import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORIES } from "../data/site";
import ProductCard from "../components/ProductCard";
import ProductDetailModal from "../components/ProductDetailModal";

export default function Products() {
    const [products, setProducts] = useState([]);
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data));
    }, []);

    const filtered = useMemo(() => {
        return products.filter((p) => {
            if (category !== "all" && p.category !== category) return false;
            if (q && !`${p.name} ${p.brand} ${p.description}`.toLowerCase().includes(q.toLowerCase())) return false;
            return true;
        });
    }, [products, category, q]);

    return (
        <>
            <section className="relative overflow-hidden">
                <div className="hero-glow" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-20 pb-10">
                    <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Catalog</div>
                    <h1 className="mt-3 font-display font-extrabold text-slate-900 text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl">
                        Phones & accessories we'd recommend to family.
                    </h1>
                </div>
            </section>

            {/* Banner */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="rounded-3xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-between premium-shadow">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-white/70 font-semibold">In-store special</div>
                        <div className="mt-1 font-display font-bold text-xl sm:text-2xl">Free SIM activation with any new phone purchase.</div>
                    </div>
                    <div className="text-sm text-white/80">Stop by 1105 S Glendale Ave · No appointment needed</div>
                </div>
            </section>

            {/* Filters */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-12">
                <div className="flex flex-col lg:flex-row gap-5 items-stretch lg:items-center justify-between">
                    <div className="relative w-full lg:max-w-sm">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            data-testid="product-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search iPhone, charger, AirPods…"
                            className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5 lg:mx-0 lg:px-0">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.id}
                                data-testid={`category-pill-${c.id}`}
                                onClick={() => setCategory(c.id)}
                                className={`whitespace-nowrap px-4 h-10 rounded-full text-sm font-medium transition ${
                                    category === c.id
                                        ? "bg-slate-900 text-white"
                                        : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section data-testid="products-grid" className="max-w-7xl mx-auto px-5 lg:px-10 mt-10 pb-20">
                {filtered.length === 0 ? (
                    <div className="text-center py-20 text-slate-500" data-testid="empty-products">
                        No products match your search.
                    </div>
                ) : (
                    <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filtered.map((p, i) => (
                            <ProductCard key={p.id} product={p} onOpen={setActive} dataTestId={`product-card-${i}`} />
                        ))}
                    </motion.div>
                )}
            </section>

            <ProductDetailModal product={active} onClose={() => setActive(null)} />
        </>
    );
}
